import { useEffect, useState } from "react";
import styles from "./Dashboard.module.css";
import { AttachMoney, CreditCard } from "@mui/icons-material";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { customTextColor, formatShortDateTime } from "../../utils/const";
import { useDispatch, useSelector } from "react-redux";
import {
  addMyCredit,
  createCreditOrder,
  creditTransfer,
  fetchManagerConfigurations,
  fetchMyCredit,
} from "../../redux/credit/creditSlice";
import { getProfile } from "../../redux/profile/profileSlice";
import { FaUser } from "react-icons/fa6";
import { toast } from "react-toastify";
import Sidebar from "./CreditHistory";
import { PiTreeDuotone } from "react-icons/pi";
import { FaHistory } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";

// Razorpay script loader
const loadRazorpayScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const fieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": { paddingLeft: "55px" },
  "& .MuiInputLabel-root": { left: "55px" },
  "& .MuiInputBase-input": { paddingBottom: "10px" },
  "& .MuiInputLabel-shrink": { left: "55px" },
};

const Dashboard = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const myCredit = useSelector((state) => state.credit.creditDetails?.data);
  const profile = useSelector((state) => state?.profile?.data?.profile);
  const managerConfigurations = useSelector(
    (state) => state.credit.managerConfigurations
  );

  const creditValue =
    managerConfigurations?.filter(
      (data) => data.name === "no_of_credit_per_dollar"
    )?.[0]?.value || 0;

  const [isTransferMode, setIsTransferMode] = useState(true);
  const [amount, setAmount] = useState("");
  const [credit, setCredit] = useState("");
  const [disableAmount, setDisableAmount] = useState(false);
  const [disableCredit, setDisableCredit] = useState(false);
  const [transferCredit, setTransferCredit] = useState("");
  const [transferUserId, setTransferUserId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  console.log(profile, myCredit);
  console.log("amount", amount);
  console.log("credit", credit);

  useEffect(() => {
    dispatch(fetchMyCredit());
    dispatch(getProfile());
    dispatch(fetchManagerConfigurations());
  }, [dispatch]);

  const handleToggle = () => {
    setIsTransferMode((prevMode) => !prevMode);
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    // Allow only numbers and limit the input to 3 digits
    if (/^\d{0,3}(\.\d{0,3})?$/.test(value)) {
      let numberValue = parseFloat(value);
      // Ensure values greater than 0 but less than 1 round up to 1
      if (numberValue > 0 && numberValue < 1) {
        numberValue = 1;
      }
      // Prevent negative values
      if (numberValue < 0) {
        setError("Amount cannot be negative");
        numberValue = 0;
      } else {
        setError("");
      }
      setAmount(numberValue);
      setCredit(numberValue ? (numberValue * creditValue).toFixed(0) : "0"); // Calculate credit
      setDisableCredit(!!numberValue);
      setDisableAmount(false);
    }
  };

  const handleCreditChange = (e) => {
    let value = e.target.value;
    // Allow only numbers and limit the input to 5 digits
    if (/^\d{0,5}$/.test(value)) {
      let numberValue = parseFloat(value);
      // Prevent negative values
      if (numberValue < 0) {
        setError("Credit cannot be negative");
        numberValue = 0;
      } else {
        setError("");
      }
      setCredit(numberValue);
      // Calculate amount; enforce minimum of 1 dollar if credit > 0 but amount is < 1
      let calculatedAmount = numberValue ? numberValue / creditValue : 0;
      if (numberValue > 0 && calculatedAmount < 1) {
        calculatedAmount = 1;
      }
      setAmount(calculatedAmount.toFixed(0));
      setDisableAmount(!!numberValue);
      setDisableCredit(false);
    }
    setLoading(false);
  };

  // Payment logic for Razorpay
  const handlePayment = async () => {
    // Start loading
    setLoading(true);

    // Validate the amount
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid amount");
      setLoading(false); // Stop loading in case of error
      return;
    }

    // Load Razorpay script
    const loaded = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!loaded) {
      alert(
        "Failed to load Razorpay script. Please check your internet connection."
      );
      setLoading(false); // Stop loading in case of script loading failure
      return;
    }

    const options = {
      key: "rzp_test_epr1KvG8SLtVdK", // Replace with your Razorpay Key ID
      amount: Number(amount) * 100, // Amount in paise (e.g., ₹2000 = 2000 * 100)
      currency: "INR",
      name: "Povital Pvt Ltd",
      description: `Credit payment of ${credit} for User ID: ${profile?.user_id}`,
      handler: (response) => {
        console.warn(response);
        toast.success(
          `Payment successful! Payment ID: ${response.razorpay_payment_id}`
        );

        const paymentData = {
          amount: Number(amount),
          currency: "INR",
          user_name: profile?.username,
          contact: profile?.mobile_no,
          status: response.razorpay_payment_id ? "successful" : "failed",
        };

        // Dispatching the payment data to the Redux store
        dispatch(createCreditOrder(paymentData)).then(() => {
          dispatch(
            addMyCredit({
              credit_type: "self_added",
              amount: amount || 0,
            })
          );
          dispatch(fetchMyCredit());
        });
      },
      prefill: {
        name: profile?.full_name || "John Doe", // Use dynamic user data
        email: profile?.email || "johndoe@example.com",
        contact: profile?.contact || "9999999999",
      },
      theme: {
        color: "#3399cc",
      },
    };

    // Create the Razorpay object and open the payment modal
    const paymentObject = new window.Razorpay(options);
    paymentObject.open();

    // Stop loading once the Razorpay modal is opened or if there's an error
    setLoading(false);
  };

  // Handle credit transfer
  const handleCreditTransfer = () => {
    // Start loading
    setLoading(true);

    // Input validation
    if (!transferCredit || !transferUserId) {
      setError("Please enter both credit amount and user ID");
      setLoading(false); // Stop loading after error
      return;
    }

    if (parseFloat(transferCredit) <= 0) {
      setError("Credit amount must be greater than zero");
      setLoading(false); // Stop loading after error
      return;
    }

    // Dispatching the credit transfer action with required data
    const transferData = {
      user_id: transferUserId,
      credit: transferCredit,
    };

    dispatch(creditTransfer(transferData))
      .then(() => {
        // Success - reset error and input fields
        setError("");
        setTransferCredit("");
        setTransferUserId("");
        // Handle any success action here (like showing a success message)
      })
      .catch((err) => {
        // Error handling
        setError("Failed to transfer credit. Please try again.");
      })
      .finally(() => {
        // Stop loading after the transfer is completed (either success or failure)
        setLoading(false);
      });
  };

  return (
    <>
      <Box className={styles.credit_profile}>
        <div>
          <img src={profile?.profile_picture} alt="Profile" />
          <span style={{ color: customTextColor }}>
            <Typography>
              Recruiter: <b>{profile?.full_name}</b>
            </Typography>
            <Typography>
              Designation: <b>{profile?.designation}</b>
            </Typography>
            <Typography>
              Company: <b>{profile?.company_name}</b>
            </Typography>
          </span>
        </div>
        <Box
          sx={{
            alignSelf: "flex-end",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              ml: 18,
            }}
          >
            <Tooltip title="View Credit History">
              <IconButton
                sx={{ mb: 1 }}
                color="primary"
                onClick={() => setIsDrawerOpen(true)}
              >
                <BsThreeDotsVertical color="gray" size={25} />
              </IconButton>
            </Tooltip>
          </Box>
          <span>
            <Button
              // variant={
              //   isTransferMode === "Add Credit" ? "contained" : "outlined"
              // }
              variant="contained"
              color="success"
              sx={{ mr: 2, textTransform: "capitalize" }}
              onClick={() => setIsTransferMode("Add Credit")}
            >
              Add Credit
            </Button>
            <Button
              // variant={
              //   isTransferMode !== "Add Credit" ? "contained" : "outlined"
              // }
              variant="contained"
              color="info"
              onClick={() => setIsTransferMode("Transfer Credit")}
              sx={{ textTransform: "capitalize" }}
            >
              Transfer Credit
            </Button>
          </span>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: "#fff", margin: "14px " }}>
        <Grid container>
          <Grid item xs={12} sm={6} sx={{ padding: "20px" }}>
            <Box
              className={styles.company_credit_box}
              sx={{ color: customTextColor }}
            >
              <Typography variant="h3">
                <b>{myCredit?.total_credits || 0}</b>
              </Typography>
              <Typography>Available Credit</Typography>
              <span>
                Last transaction{" "}
                {formatShortDateTime(myCredit?.credit_history[0]?.updated_at)}
              </span>
              <Typography>
                Expires at:{" "}
                <span style={{ color: "red" }}>
                  {formatShortDateTime(myCredit?.credit_history[0]?.expires_at)}
                </span>
              </Typography>
            </Box>
          </Grid>
          {isTransferMode === "Add Credit" ? (
            <Grid item xs={12} sm={6} sx={{ padding: "20px" }}>
              <Box sx={{ textAlign: "center" }}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{ padding: "20px 10px", position: "relative" }}
                  >
                    <Box className={styles.icon_style}>
                      <AttachMoney style={{ fontSize: "35px" }} />
                    </Box>
                    <TextField
                      label="Amount"
                      variant="standard"
                      type="number"
                      sx={fieldStyle}
                      value={amount}
                      onChange={handleAmountChange}
                      disabled={disableAmount}
                      error={!!error}
                      helperText={error}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{ padding: "20px 10px", position: "relative" }}
                  >
                    <Box className={styles.icon_style}>
                      <CreditCard style={{ fontSize: "35px" }} />
                    </Box>
                    <TextField
                      label="Credit"
                      variant="standard"
                      type="number"
                      sx={fieldStyle}
                      value={credit}
                      onChange={handleCreditChange}
                      disabled={disableCredit}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ mt: 1.8, textTransform: "capitalize" }}
                  className={styles.pay_credit_btn}
                  onClick={handlePayment}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                      sx={{ marginRight: "10px" }}
                    />
                  ) : (
                    "Pay Now"
                  )}
                </Button>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} sx={{ padding: "20px" }}>
              <Box sx={{ textAlign: "center" }}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{
                      padding: "20px 10px 10px 10px",
                      position: "relative",
                    }}
                  >
                    <Box className={styles.icon_style}>
                      <FaUser style={{ fontSize: "26px" }} />
                    </Box>
                    <TextField
                      label="Enter ID"
                      variant="standard"
                      sx={fieldStyle}
                      value={transferUserId}
                      onChange={(e) => setTransferUserId(e.target.value)}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{
                      padding: "20px 10px 10px 10px",
                      position: "relative",
                    }}
                  >
                    <Box className={styles.icon_style}>
                      <CreditCard style={{ fontSize: "35px" }} />
                    </Box>
                    <TextField
                      id="standard-basic"
                      label="Enter Credit"
                      variant="standard"
                      sx={fieldStyle}
                      value={transferCredit}
                      onChange={(e) => setTransferCredit(e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  color="info"
                  className={styles.pay_credit_btn}
                  sx={{ mt: 3, textTransform: "capitalize" }}
                  onClick={handleCreditTransfer}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      color="info"
                      sx={{ marginRight: "10px" }}
                    />
                  ) : (
                    "Transfer Now"
                  )}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
        <Sidebar
          isOpen={isDrawerOpen}
          handleClose={() => setIsDrawerOpen(false)}
          creditHistory={myCredit?.credit_history || []}
        />
      </Box>
    </>
  );
};

export default Dashboard;
